import { useEffect, useRef, useState, useMemo, useCallback } from 'react';

/**
 * Deep equality comparison for browser environments
 * Replacement for Node.js util.isDeepStrictEqual
 */
function isDeepEqual(obj1, obj2) {
  // Same reference or both null/undefined
  if (obj1 === obj2) return true;

  // Type check
  if (
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object' ||
    obj1 === null ||
    obj2 === null
  ) {
    return false;
  }

  // Array check
  if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

  // Get keys
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // Different number of keys
  if (keys1.length !== keys2.length) return false;

  // Recursively compare values
  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!isDeepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
}

/**
 * useDirtyChecker Hook
 *
 * Tracks changes between initial data and current data to prevent unnecessary API calls.
 * Uses deep comparison to detect actual changes in nested objects/arrays.
 * Supports custom validation with detailed error reporting.
 *
 * @param {Object} initialData - The original/initial data object
 * @param {Object} currentData - The current/modified data object
 * @param {Object} options - Configuration options
 * @param {Function} options.validate - Custom validation function (data) => { isValid, errors: {} }
 * @param {number} options.validationDelay - Debounce delay in ms (default: 500ms)
 * @returns {Object} - { isDirty, changedFields, isValid, validationErrors: {}, reset, getInitialData, validateNow }
 *
 * @example
 * // Basic usage without validation
 * const { isDirty, changedFields, reset } = useDirtyChecker(
 *   initialProfileData,
 *   currentProfileData
 * );
 *
 * @example
 * // With custom validation (object format)
 * const { isDirty, changedFields, isValid, validationErrors, reset } = useDirtyChecker(
 *   initialProfileData,
 *   currentProfileData,
 *   {
 *     validate: (data) => {
 *       const errors = {};
 *       if (data.firstName && data.firstName.length > 50) {
 *         errors.firstName = 'First name must be 50 characters or less';
 *       }
 *       if (data.bio && data.bio.length > 1000) {
 *         errors.bio = 'Bio must be 1000 characters or less';
 *       }
 *       return { isValid: Object.keys(errors).length === 0, errors };
 *     }
 *   }
 * );
 *
 * // Only call API if data changed AND is valid
 * if (isDirty && isValid) {
 *   await saveProfile(currentProfileData);
 *   reset(currentProfileData);
 * } else if (!isValid) {
 *   console.error('Validation errors:', validationErrors); // { firstName: '...', bio: '...' }
 * }
 *
 * @example
 * // Using with profileUtils.validateProfileData
 * import { profileUtils } from 'api/services/profile';
 *
 * const { isDirty, isValid, validationErrors } = useDirtyChecker(
 *   initialProfileData,
 *   currentProfileData,
 *   { validate: profileUtils.validateProfileData }
 * );
 *
 * @example
 * // With custom validation delay (faster validation)
 * const { isDirty, isValid, validationErrors } = useDirtyChecker(
 *   initialData,
 *   currentData,
 *   {
 *     validate: profileUtils.validateProfileData,
 *     validationDelay: 300 // Validate after 300ms of no typing
 *   }
 * );
 */
export function useDirtyChecker(initialData, currentData, options = {}) {
  const { validate = null, validationDelay = 500 } = options;
  const initialRef = useRef(initialData);
  const prevCurrentRef = useRef(currentData);

  const [isDirty, setIsDirty] = useState(false);
  const [changedFields, setChangedFields] = useState([]);
  const [isValid, setIsValid] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const validationTimeoutRef = useRef(null);

  // Only update if data actually changed (shallow comparison first)
  const memoCurrent = useMemo(() => {
    if (prevCurrentRef.current === currentData) {
      return prevCurrentRef.current;
    }
    prevCurrentRef.current = currentData;
    return currentData;
  }, [currentData]);

  // Run validation with debounce to prevent validation on every keystroke
  useEffect(() => {
    if (!validate || !memoCurrent) {
      setIsValid(true);
      setValidationErrors({});
      return;
    }

    // Clear previous timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    // Debounce validation
    validationTimeoutRef.current = setTimeout(() => {
      try {
        const result = validate(memoCurrent);

        // Support both { isValid, errors } formats (array or object)
        if (typeof result === 'object' && result !== null) {
          const newIsValid = result.isValid ?? true;
          const newErrors = result.errors || {};

          // Only update state if values actually changed (prevent re-renders)
          setIsValid((prev) => (prev !== newIsValid ? newIsValid : prev));
          setValidationErrors((prev) => {
            // Deep compare errors to prevent unnecessary updates
            if (isDeepEqual(prev, newErrors)) return prev;
            return newErrors;
          });
        } else {
          // If validation returns boolean, treat as isValid
          setIsValid(!!result);
          setValidationErrors({});
        }
      } catch (error) {
        console.error('Validation function error:', error);
        setIsValid(false);
        setValidationErrors({ _error: `Validation error: ${error.message}` });
      }
    }, validationDelay);

    // Cleanup timeout on unmount or data change
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, [memoCurrent, validate, validationDelay]);

  useEffect(() => {
    // Skip comparison if either data is null/undefined
    if (!initialRef.current || !memoCurrent) {
      setIsDirty(false);
      setChangedFields([]);
      return;
    }

    const diffs = [];

    // Compare all keys from both initial and current data
    const allKeys = new Set([
      ...Object.keys(initialRef.current),
      ...Object.keys(memoCurrent),
    ]);

    allKeys.forEach((key) => {
      if (!isDeepEqual(initialRef.current[key], memoCurrent[key])) {
        diffs.push(key);
      }
    });

    // Only update state if values actually changed (prevent re-renders)
    setChangedFields((prev) => {
      if (isDeepEqual(prev, diffs)) return prev;
      return diffs;
    });
    setIsDirty(diffs.length > 0);
  }, [memoCurrent]);

  /**
   * Reset the dirty state with new baseline data
   * @param {Object} newInitial - New baseline data (defaults to current data)
   */
  const reset = useCallback(
    (newInitial) => {
      // Clear pending validation timeout
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
        validationTimeoutRef.current = null;
      }

      const baseline = newInitial || memoCurrent;
      initialRef.current = baseline;
      prevCurrentRef.current = baseline;
      setIsDirty(false);
      setChangedFields([]);
      setIsValid(true);
      setValidationErrors({});
    },
    [memoCurrent]
  );

  /**
   * Get the current initial/baseline data
   * @returns {Object} The initial data being compared against
   */
  const getInitialData = useCallback(() => initialRef.current, []);

  /**
   * Run validation immediately (bypass debounce)
   * Useful for validating before save
   * @returns {Object} { isValid, errors }
   */
  const validateNow = useCallback(() => {
    if (!validate || !memoCurrent) {
      return { isValid: true, errors: {} };
    }

    try {
      const result = validate(memoCurrent);

      if (typeof result === 'object' && result !== null) {
        const newIsValid = result.isValid ?? true;
        const newErrors = result.errors || {};

        // Update state immediately
        setIsValid(newIsValid);
        setValidationErrors(newErrors);

        return { isValid: newIsValid, errors: newErrors };
      } else {
        const isValidResult = !!result;
        setIsValid(isValidResult);
        setValidationErrors({});
        return { isValid: isValidResult, errors: {} };
      }
    } catch (error) {
      console.error('Validation function error:', error);
      const errorObj = { _error: `Validation error: ${error.message}` };
      setIsValid(false);
      setValidationErrors(errorObj);
      return { isValid: false, errors: errorObj };
    }
  }, [validate, memoCurrent]);

  // Memoize return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      isDirty,
      changedFields,
      isValid,
      validationErrors,
      reset,
      getInitialData,
      validateNow,
    }),
    [
      isDirty,
      changedFields,
      isValid,
      validationErrors,
      reset,
      getInitialData,
      validateNow,
    ]
  );
}
